import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';

// Toast shape: { id, message, intent, duration, createdAt, action, onClose }
// intent: 'neutral' | 'info' | 'success' | 'warning' | 'danger'
// action: { label, onClick }

const ToastContext = createContext(null);

let idCounter = 0;
function nextId() { idCounter = (idCounter + 1) % Number.MAX_SAFE_INTEGER; return `t${Date.now()}_${idCounter}`; }

function reducer(state, action) {
  switch(action.type) {
    case 'ADD':
      return [...state, action.toast];
    case 'REMOVE':
      return state.filter(t => t.id !== action.id);
    case 'CLEAR':
      return [];
    default:
      return state;
  }
}

export function ToastProvider({ children, max = 5 }) {
  const [toasts, dispatch] = useReducer(reducer, []);
  // Track timers so we can cancel when toast manually closed / hovered
  const timersRef = useRef(new Map());

  // Cleanup timers when provider unmounts
  useEffect(() => () => {
    for (const timeoutId of timersRef.current.values()) clearTimeout(timeoutId);
    timersRef.current.clear();
  }, []);

  const removeToast = useCallback((id) => {
    if (timersRef.current.has(id)) {
      clearTimeout(timersRef.current.get(id));
      timersRef.current.delete(id);
    }
    dispatch({ type: 'REMOVE', id });
  }, []);

  const addToast = useCallback((opts) => {
    const {
      message,
      intent = 'neutral',
      duration = 4000,
      action,
      onClose
    } = opts || {};

    if (!message) return null;
    // Enforce max by removing oldest when exceeding
    const id = nextId();
    const toast = { id, message, intent, duration, createdAt: Date.now(), action, onClose };
    dispatch({ type: 'ADD', toast });
    if (duration > 0) {
      const timeoutId = setTimeout(() => {
        removeToast(id);
        if (onClose) onClose();
      }, duration);
      timersRef.current.set(id, timeoutId);
    }
    return id;
  }, [removeToast]);

  const clearToasts = useCallback(() => {
    for (const timeoutId of timersRef.current.values()) clearTimeout(timeoutId);
    timersRef.current.clear();
    dispatch({ type: 'CLEAR' });
  }, []);

  const pauseTimer = useCallback((id) => {
    if (!timersRef.current.has(id)) return;
    clearTimeout(timersRef.current.get(id));
    timersRef.current.delete(id);
  }, []);
  const resumeTimer = useCallback((id) => {
    const toast = toasts.find(t => t.id === id);
    if (!toast || toast.duration <= 0) return;
    const remaining = Math.max(0, toast.duration - (Date.now() - toast.createdAt));
    const timeoutId = setTimeout(() => {
      removeToast(id);
      if (toast.onClose) toast.onClose();
    }, remaining);
    timersRef.current.set(id, timeoutId);
  }, [toasts, removeToast]);

  const value = {
    toasts,
    addToast,
    removeToast,
    clearToasts,
    pauseTimer,
    resumeTimer
  };

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
